import {
  AutomaticNamespaceProvisioningFormValues,
  generateNamespace,
  isUserInScope,
  USER_SCOPE_OPTIONS,
} from '@/react/portainer/policies/components/kubernetes/automatic-namespace-provisioning/types';
import { CurrentUserResponse } from '@/portainer/users/queries/useLoadCurrentUser';

import { FormSection } from '@@/form-components/FormSection';
import { Badge } from '@@/Badge';

import { PolicyEmptyState } from './PolicyEmptyState';
import { PolicyTableWrapper } from './PolicyTableWrapper';

type CurrentUser = Pick<
  CurrentUserResponse,
  'Id' | 'Username' | 'Role' | 'LastAuthenticationMethod'
>;

interface Props {
  formData: AutomaticNamespaceProvisioningFormValues;
  roleName?: string;
  currentUser: CurrentUser;
}

export function AutomaticNamespaceProvisioningPolicyReadOnly({
  formData,
  roleName = '',
  currentUser,
}: Props) {
  const userScopes = USER_SCOPE_OPTIONS.filter((option) =>
    formData.userScopes.includes(option.value)
  )
    .map((option) => option.label)
    .join(', ');

  return (
    <>
      <FormSection title="Namespace provisioning">
        <PolicyTableWrapper
          caption="Automatic namespace provisioning settings"
          data-cy="anp-policy-readonly-table"
        >
          <tbody className="divide-y divide-gray-5 th-highcontrast:divide-gray-7 th-dark:divide-gray-7">
            <SettingRow label="Authentication methods" value={userScopes} />
            <SettingRow
              label="Namespace pattern"
              value={formData.namespacePattern}
            />
            <SettingRow label="Role" value={roleName} />
          </tbody>
        </PolicyTableWrapper>
      </FormSection>

      <AccessList
        formData={formData}
        roleName={roleName}
        currentUser={currentUser}
      />
    </>
  );
}

function AccessList({
  formData,
  roleName,
  currentUser,
}: {
  formData: AutomaticNamespaceProvisioningFormValues;
  roleName: string;
  currentUser: CurrentUser;
}) {
  if (
    !isUserInScope(
      formData.userScopes,
      currentUser.Id,
      currentUser.Role,
      currentUser.LastAuthenticationMethod
    )
  ) {
    return (
      <PolicyEmptyState
        title="Access List"
        message="This policy does not provision a namespace for your account."
      />
    );
  }

  return (
    <FormSection title="Access List">
      <PolicyTableWrapper
        caption="Your access granted by this automatic namespace provisioning policy"
        data-cy="anp-policy-access-table"
      >
        <thead className="bg-gray-3 th-highcontrast:bg-gray-9 th-dark:bg-gray-9">
          <tr>
            <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
            <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
            <th className="px-4 py-3 text-left text-sm font-medium">Role</th>
            <th className="px-4 py-3 text-left text-sm font-medium">
              Namespaces
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-5 th-highcontrast:divide-gray-7 th-dark:divide-gray-7">
          <tr className="bg-gray-2 th-highcontrast:bg-black th-dark:bg-gray-iron-10">
            <td className="px-4 py-3 text-sm font-medium">
              {currentUser.Username}
            </td>
            <td className="px-4 py-3">
              <Badge type="info">User</Badge>
            </td>
            <td className="px-4 py-3 text-sm">{roleName}</td>
            <td className="px-4 py-3 text-sm font-medium">
              {generateNamespace(formData.namespacePattern, currentUser.Id)}
            </td>
          </tr>
        </tbody>
      </PolicyTableWrapper>
    </FormSection>
  );
}

function SettingRow({ label, value }: { label: string; value: string }) {
  return (
    <tr className="bg-gray-2 th-highcontrast:bg-black th-dark:bg-gray-iron-10">
      <td className="px-4 py-3 text-sm font-medium">{label}</td>
      <td className="px-4 py-3 text-sm">{value}</td>
    </tr>
  );
}
